import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Row, Col, Button, Typography, Card, Spin } from "antd";
import { SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { Formik, Form } from "formik";
import * as yup from "yup";

import BackButton from "components/BackButton";
import { TextInput, TextEditor } from "components/common";
import {
  getHealthInsuranceContentRequest,
  updateHealthInsuranceRequest,
} from "providers/HealthInsuranceProvider/slice";
import "./styles.less";

const { Title } = Typography;

// Validation schema
const validationSchema = yup.object().shape({
  title: yup.string().required("Vui lòng nhập tiêu đề"),
  content: yup.string().required("Vui lòng nhập nội dung"),
});

const HealthInsurance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formikRef = useRef(null);

  const { content, isLoading, isSaving } = useSelector(
    (state) => state.healthInsurance
  );

  // Load content on mount
  useEffect(() => {
    dispatch(getHealthInsuranceContentRequest());
  }, [dispatch]);

  // Initial form values
  const initialValues = {
    title: content?.title || "Bảo hiểm - Quy trình",
    content: content?.content || "",
  };

  // Handle form submit
  const handleSubmit = (values) => {
    dispatch(updateHealthInsuranceRequest(values));
  };

  // Update form values when content loads
  useEffect(() => {
    if (content && formikRef.current) {
      formikRef.current.setFieldValue("title", content.title);
      formikRef.current.setFieldValue("content", content.content);
    }
  }, [content]);

  return (
    <div className="health-insurance-wrapper">
      <BackButton onClick={() => navigate(-1)} />

      <Card className="health-insurance-card">
        <Row className="health-insurance-header" align="middle" justify="space-between">
          <Title level={3}>Quản lý nội dung Bảo hiểm - Quy trình</Title>
        </Row>

        {isLoading ? (
          <div className="loading-container">
            <Spin size="large" />
          </div>
        ) : (
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form>
                <Row gutter={[24, 24]}>
                  <Col span={24}>
                    <TextInput
                      label="Tiêu đề"
                      name="title"
                      placeholder="Nhập tiêu đề"
                      required
                      size="large"
                    />
                  </Col>

                  <Col span={24}>
                    <TextEditor
                      label="Nội dung"
                      name="content"
                      placeholder="Nhập nội dung chi tiết về quy trình bảo hiểm y tế..."
                      required
                      height={500}
                    />
                    <div className="content-note">
                      <small className="text-muted">
                        * Nội dung này sẽ được hiển thị cho người dùng trên ứng dụng di động.
                        Bạn có thể sử dụng các công cụ định dạng để tạo nội dung dễ đọc và chuyên nghiệp.
                      </small>
                    </div>
                  </Col>

                  <Col span={24}>
                    <div className="form-actions">
                      <Button
                        type="default"
                        size="large"
                        onClick={() => navigate(-1)}
                        disabled={isSaving}
                      >
                        Hủy
                      </Button>
                      <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={isSaving}
                      >
                        Lưu thay đổi
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        )}

        {content && (
          <div className="last-updated">
            <small className="text-muted">
              Cập nhật lần cuối: {new Date(content.updatedAt).toLocaleString("vi-VN")}
            </small>
          </div>
        )}
      </Card>
    </div>
  );
};

export default HealthInsurance;
